import { v4 as uuid } from "uuid";
import {
	getBearerToken,
	getRefreshToken,
	getRevision,
	setBearerToken,
	setRefreshToken,
	setRevision,
} from "../utils/storage";
import { InvalidRevisionError } from "./errors";
import type { SveaRequestInit } from "./types";

type HeaderArg = [string, string];

const getAuthHeader = (): HeaderArg => ["Authorization", `Bearer ${getBearerToken()}`];
const getRevisionHeader = (): HeaderArg => ["If-Match", getRevision()];

export const unwrapJsonResponse = (response: Response) => {
	switch (response.headers.get("content-type")) {
		case "application/json":
			return response.json();
		default:
			return response.text();
	}
};

const getSessionId = () => {
	const sessionId = sessionStorage.getItem("sessionId");

	if (!sessionId) {
		const newSessionId = uuid();
		sessionStorage.setItem("sessionId", newSessionId);
		return newSessionId;
	}
	return sessionId;
};

const refreshTokenIfFailed = async (baseUrl: string, error: Error) => {
	if (error instanceof InvalidRevisionError) {
		const { request } = error;
		if (!request) throw new InvalidRevisionError();

		const refreshToken = getRefreshToken();
		if (!refreshToken) throw new InvalidRevisionError();

		const fullUrl = new URL("/auth/refresh", baseUrl);

		const res = await fetch(fullUrl, {
			method: "POST",
			body: JSON.stringify({ refreshToken }),
		}).then(unwrapJsonResponse);

		setBearerToken(res.bearerToken);
		setRefreshToken(res.refreshToken);

		return fetch(new URL(request.url, baseUrl), request.init);
	}

	throw error;
};

const handleApiError = (url: string, init?: SveaRequestInit) => (response: Response) => {
	switch (true) {
		case response.status === 401:
			return Promise.reject(new InvalidRevisionError("Unexpected revision", url, init));
		case response.status >= 400:
			return Promise.reject(response);
		default:
			return response;
	}
};

export const sveaFetch = async (
	baseUrl: string,
	url: string,
	appVersion: string,
	init?: SveaRequestInit,
) => {
	const headers = new Headers();

	headers.set("X-Svea-SessionId", getSessionId());

	if (init?.withAuth) headers.set(...getAuthHeader());
	if (init?.withRevision) headers.set(...getRevisionHeader());

	headers.set("X-Svea-Clientapp", appVersion);

	const fullUrl = new URL(url, baseUrl);
	return fetch(fullUrl.toString(), {
		...init,
		headers,
	})
		.then(handleApiError(url, init))
		.catch((e) => refreshTokenIfFailed(baseUrl, e))
		.then((res) => {
			const etag = res.headers.get("ETag");
			if (etag) setRevision(etag);
			return res;
		});
};
